import { type CompanyDetailsDto } from '@/api/generated/models/company-details-dto';
import { PrivacyPolicySignStatusDto } from '@/api/generated/models/privacy-policy-sign-status-dto';
import { type VacancyCandidateDetailsDto } from '@/api/generated/models/vacancy-candidate-details-dto';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import {
  type EntityBreadcrumbName,
  RouteEnum,
  type RouteModel,
} from '@/layouts/routes/Route.model';

const loadViewVacancyEdit = () => import('@/views/vacancyEdit/VacancyEdit.view.tsx');
const loadViewSignPrivacyPolicy = () => import('@/views/signPrivacyPolicy/SignPrivacyPolicy.view');

/**
 * Entity names for the breadcrumb leaf (OCD-311): read from the query cache
 * the details views already populate — the declarations never fetch. The
 * candidate name rides on `CANDIDATE_DETAILS`, which `useGetCandidateDetails`
 * mirrors into the cache from its imperative fetch.
 */
const candidateEntityName: EntityBreadcrumbName = {
  param: 'candidateId',
  queryKey: candidateId => [QueryKeyEnum.CANDIDATE_DETAILS, candidateId],
  select: (candidate?: VacancyCandidateDetailsDto) =>
    [candidate?.firstName, candidate?.lastName].filter(Boolean).join(' ') || null,
};

export const ROUTES: RouteModel[] = [
  {
    breadcrumb: { label: 'profileSearch' },
    loadView: () => import('@/views/profileSearchNew/ProfileSearchNew.view.tsx'),
    path: RouteEnum.PROFILE_SEARCH,
  },
  {
    breadcrumb: { label: 'profileSearch' },
    loadView: () => import('@/views/profileSearchNew/ProfileSearchNew.view.tsx'),
    path: RouteEnum.PROFILE_SEARCH_NEW,
  },
  {
    breadcrumb: { label: 'assessments' },
    loadView: () => import('@/views/assessments/list/Assessments.view.tsx'),
    path: RouteEnum.ASSESSMENTS,
  },
  {
    breadcrumb: { label: 'assessments', parent: RouteEnum.ASSESSMENTS },
    loadView: () => import('@/views/assessments/form/AssessmentForm.view.tsx'),
    path: RouteEnum.ASSESSMENT_ID,
  },
  {
    breadcrumb: { label: 'assessments', parent: RouteEnum.ASSESSMENT_ID },
    loadView: () => import('@/views/assessments/finish/AssessmentEdit.view.tsx'),
    path: RouteEnum.ASSESSMENT_EDIT,
  },
  {
    loadView: () => import('@/views/signInHandler/SignInHandler.tsx'),
    path: RouteEnum.ROOT,
  },
  {
    breadcrumb: { label: 'recruitmentCandidates' },
    loadView: () => import('@/views/candidates/list/Candidates.view'),
    path: RouteEnum.CANDIDATES,
  },
  {
    breadcrumb: {
      entityName: candidateEntityName,
      label: 'candidateDetails',
      parent: RouteEnum.CANDIDATES,
    },
    loadView: () =>
      import('@/views/vacancyCandidateDetails/wrapper/VacancyCandidateDetails.wrapper'),
    path: RouteEnum.CANDIDATES_ID,
  },
  {
    breadcrumb: { label: 'companies' },
    loadView: () => import('@/views/companies/list/Companies.view'),
    path: RouteEnum.COMPANIES,
  },
  {
    breadcrumb: {
      entityName: {
        param: 'id',
        queryKey: companyId => [QueryKeyEnum.COMPANY_DETAILS, companyId],
        select: (company?: CompanyDetailsDto) => company?.companyName ?? null,
      },
      label: 'companyDetails',
      parent: RouteEnum.COMPANIES,
    },
    loadView: () => import('@/views/companies/details/CompanyDetails.view.tsx'),
    path: RouteEnum.COMPANIES_ID,
  },
  {
    breadcrumb: { label: 'companyEmployee', parent: RouteEnum.COMPANIES_ID },
    loadView: () => import('@/views/userDetails/UserDetails.view.tsx'),
    path: RouteEnum.COMPANY_EMPLOYEE,
  },
  {
    breadcrumb: { label: 'userProfile' },
    loadView: () => import('@/views/userDetails/UserDetails.view.tsx'),
    path: RouteEnum.MY_PROFILE,
  },
  {
    breadcrumb: { label: 'overview' },
    loadView: () => import('@/views/overview/Overview.view.tsx'),
    path: RouteEnum.OVERVIEW,
  },
  {
    breadcrumb: { label: 'knowMe' },
    loadView: () =>
      import('@/views/generalError/forbidden/Forbidden.comp.tsx').then(module => ({
        default: module.Forbidden,
      })),
    path: RouteEnum.FORBIDDEN,
  },
  {
    breadcrumb: { label: 'knowMe' },
    loadView: () =>
      import('@/views/generalError/notAuthorized/NotFound.comp').then(module => ({
        default: module.NotFound,
      })),
    path: RouteEnum.NOT_FOUND,
  },
  {
    breadcrumb: { label: 'knowMe' },
    loadView: () => import('@/views/generalError/notFound/NotAuthorized.view'),
    path: RouteEnum.NOT_AUTHORIZED,
  },
  {
    breadcrumb: { label: 'knowMe' },
    loadView: () =>
      import('@/views/maintenance/MaintenanceRoute.view').then(module => ({
        default: module.MaintenanceRouteView,
      })),
    path: RouteEnum.MAINTENANCE,
  },
  {
    breadcrumb: { label: 'knowMe' },
    loadView: () =>
      import('@/views/generalError/waitingForApproval/WaitingForApproval.view').then(module => ({
        default: module.WaitingForApprovalView,
      })),
    path: RouteEnum.WAITING_FOR_APPROVAL,
  },
  {
    breadcrumb: { label: 'knowMe' },
    loadView: () =>
      import('@/views/loginSuccessful/LoginSuccessful.view').then(module => ({
        default: module.LoginSuccessfulView,
      })),
    path: RouteEnum.LOGIN_SUCCESSFUL,
  },
  {
    breadcrumb: { label: 'projects' },
    loadView: () => import('@/views/vacancyProjects/list/VacancyProjects.view.tsx'),
    path: RouteEnum.VACANCY_PROJECTS,
  },
  {
    breadcrumb: { label: 'projectDetails', parent: RouteEnum.VACANCY_PROJECTS },
    loadView: () => import('@/views/vacancyProjects/details/VacancyProjectDetails.view'),
    path: RouteEnum.PROJECT_DETAILS,
  },
  {
    breadcrumb: { label: 'recruitmentBoard' },
    loadView: () => import('@/views/recruitmentBoard/RecruitmentBoard.view'),
    path: RouteEnum.RECRUITMENT_BOARD,
  },
  {
    breadcrumb: { label: 'userProfile' },
    loadView: () => import('@/views/userProfile/UserProfile.view.tsx'),
    path: RouteEnum.USER_PROFILE,
  },
  {
    breadcrumb: { label: 'settingsDictionaries' },
    loadView: () => import('@/views/dictionaries/list/DictionariesList.view.tsx'),
    path: RouteEnum.DICTIONARIES,
  },
  {
    breadcrumb: {
      dynamicTitle: route =>
        (route.location.state as { copyMode?: boolean } | null)?.copyMode
          ? 'copyVacancy'
          : 'addVacancy',
      label: 'addVacancy',
    },
    loadView: loadViewVacancyEdit,
    path: RouteEnum.ADD_VACANCY,
  },
  {
    breadcrumb: {
      dynamicTitle: () => 'editVacancy',
      label: 'editVacancy',
      parent: RouteEnum.VACANCY_ID,
    },
    loadView: loadViewVacancyEdit,
    path: RouteEnum.EDIT_VACANCY,
  },
  {
    breadcrumb: { label: 'vacancyDetails', parent: RouteEnum.RECRUITMENT_BOARD },
    loadView: () => import('@/views/vacancyDetails/VacancyDetails.view.tsx'),
    path: RouteEnum.VACANCY_ID,
  },
  {
    breadcrumb: {
      dynamicParent: state =>
        (state as { goesFrom?: string } | null)?.goesFrom === RouteEnum.CANDIDATES
          ? RouteEnum.CANDIDATES
          : undefined,
      entityName: candidateEntityName,
      label: 'candidateDetails',
      parent: RouteEnum.VACANCY_ID,
    },
    loadView: () =>
      import('@/views/vacancyCandidateDetails/wrapper/VacancyCandidateDetails.wrapper'),
    path: RouteEnum.VACANCIES_CANDIDATE,
  },
  {
    breadcrumb: { label: 'initializeAccount' },
    loadView: () => import('@/views/userInitialize/UserInitialize.view.tsx'),
    path: RouteEnum.USERS_INITIALIZE,
  },
  {
    breadcrumb: { label: 'reports' },
    loadView: () => import('@/views/reports/Reports.view'),
    path: RouteEnum.REPORTS,
  },
  {
    breadcrumb: { label: 'DICTIONARY_MANAGEMENT_SKILL' },
    loadView: () =>
      import('@/views/dictionaryManagement/skillsManagement/SkillsManagement.view.tsx'),
    path: RouteEnum.SKILLS_MANAGEMENT,
  },
  {
    breadcrumb: { label: 'DICTIONARY_MANAGEMENT_OFFERING' },
    loadView: () =>
      import('@/views/dictionaryManagement/offeringsManagement/OfferingsManagement.view.tsx'),
    path: RouteEnum.OFFERINGS_MANAGEMENT,
  },
  {
    breadcrumb: { label: 'DICTIONARY_MANAGEMENT_CERTIFICATE' },
    loadView: () =>
      import('@/views/dictionaryManagement/certificatesManagement/CertificatesManagement.view.tsx'),
    path: RouteEnum.CERTIFICATES_MANAGEMENT,
  },
  {
    breadcrumb: {
      dynamicTitle: (_route, { privacyPolicyStatus }) =>
        privacyPolicyStatus === PrivacyPolicySignStatusDto.SignedOutdated
          ? 'signUpdatedPrivacyPolicy'
          : 'signPrivacyPolicy',
      label: 'signPrivacyPolicy',
    },
    loadView: loadViewSignPrivacyPolicy,
    path: RouteEnum.USERS_SIGN_PRIVACY_POLICY,
  },
  {
    loadView: () => import('@/views/login/Login.view'),
    path: RouteEnum.LOGIN,
  },
];
